import { Mailbox, Pencil, Play, Plus, Trash2, X } from "lucide-mui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { api, type ProspectOrderDetail, type ProspectOrderWorkflow } from "../../../api/client.ts";
import NoSelectionPrompt from "../../../components/NoSelectionPrompt.tsx";
import { keys } from "../../../api/queryKeys.ts";
import CenteredSpinner from "../../../components/CenteredSpinner.tsx";
import ConfirmDialog from "../../../components/ConfirmDialog.tsx";
import DetailRow from "../../../components/DetailRow.tsx";
import FormattedDate from "../../../components/FormattedDate.tsx";
import Panel from "../../../components/Panel.tsx";
import RelativeTime from "../../../components/RelativeTime.tsx";
import RowActions from "../../../components/RowActions.tsx";
import SectionHeading from "../../../components/SectionHeading.tsx";
import StatusChip from "../../../components/StatusChip.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";
import UserAvatar from "../../../components/UserAvatar.tsx";
import { useOrganization } from "../../../contexts/OrganizationContext.tsx";
import { collectionPath, pathForTenant } from "../../../contexts/TenantContext.tsx";
import toast from "react-hot-toast";
import ListSelector from "../../../components/ListSelector.tsx";
import AgentRunRow from "../../../components/agent/AgentRunRow.tsx";
import { useCanSeeTranscripts } from "../../../components/agent/useCanSeeTranscripts.ts";
import ExecutionStatusChip from "../../../components/workflows/ExecutionStatusChip.tsx";
import StepApprovalBanner from "../../../components/workflows/StepApprovalBanner.tsx";
import PageHeader from "../../../components/PageHeader.tsx";
import DetailPageStatus from "../../../components/detail/DetailPageStatus.tsx";
import AssigneePicker from "./AssigneePicker.tsx";
import OrderFormDialog from "./OrderFormDialog.tsx";
import { STATUS_LABELS } from "./orderConstants.ts";
import { formatNumber } from "../../../utils/format.ts";
import { isJobActive } from "../../../utils/status.ts";
import { KIND_META, isActionKind } from "../../workflows/editor/types.ts";
import EmptyState from "../../../components/EmptyState.tsx";

/** One brief field: the order's own text, else a note that the tenant's is used. */
function briefText(text: string | null): ReactNode {
  if (!text?.trim()) {
    return (
      <Box component="span" sx={{ color: "text.secondary" }}>
        From the tenant's company profile
      </Box>
    );
  }
  return (
    <Box component="span" sx={{ whiteSpace: "pre-wrap" }}>
      {text}
    </Box>
  );
}

/** The order's facts side by side, wrapping to as many columns as fit. */
function DetailGrid({ minWidth, children }: { minWidth: number; children: ReactNode }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))`,
        columnGap: 4,
      }}
    >
      {children}
    </Box>
  );
}

// Where a workflow-made order came from: the workflow, and the record (a
// Notion page) the order was drafted from.
function OrderOrigin({ order }: { order: ProspectOrderDetail }) {
  const origin = order.origin;
  if (!origin) return null;
  const workflowHref =
    order.tenant_slug && origin.workflow_id ? pathForTenant(order.tenant_slug, `/workflows/${origin.workflow_id}`) : null;
  const signalHref =
    order.tenant_slug && origin.signal_id ? pathForTenant(order.tenant_slug, `/workflows/signals?signal=${origin.signal_id}`) : null;
  return (
    <Alert severity="info" variant="outlined">
      Created by the workflow{" "}
      {workflowHref ? (
        <Link component={RouterLink} to={workflowHref}>
          {origin.workflow_name ?? "(deleted workflow)"}
        </Link>
      ) : (
        <strong>{origin.workflow_name ?? "(deleted workflow)"}</strong>
      )}
      {origin.record_url || origin.record_title ? (
        <>
          {" from "}
          {origin.record_url ? (
            <Link href={origin.record_url} target="_blank" rel="noopener noreferrer">
              {origin.record_title ?? "the source record"}
            </Link>
          ) : (
            origin.record_title
          )}
          {signalHref && (
            <>
              {" ("}
              <Link component={RouterLink} to={signalHref}>
                signal
              </Link>
              {")"}
            </>
          )}
        </>
      ) : (
        signalHref && (
          <>
            {" from "}
            <Link component={RouterLink} to={signalHref}>
              a matched signal
            </Link>
          </>
        )
      )}
      . The brief was drafted from that record; edit it here if the draft missed something.
    </Alert>
  );
}

function WorkflowRow({ workflow }: { workflow: ProspectOrderWorkflow }) {
  // One chip per step in tree order; a step that never fired shows as such.
  // Executions carry no tree position, so same-typed steps pair up in order.
  const used = new Set<string>();
  const chips = workflow.step_types.map((stepType, i) => {
    const execution = workflow.executions.find((e) => e.step_type === stepType && !used.has(e.id));
    if (execution) used.add(execution.id);
    return { key: `${stepType}-${i}`, stepType, execution };
  });
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
      <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 160 }}>
        {workflow.name}
      </Typography>
      {!workflow.active && <Chip size="small" label="Paused" variant="outlined" />}
      {chips.map(({ key, stepType, execution }) => {
        const label = isActionKind(stepType) ? KIND_META[stepType].label : stepType;
        if (execution) return <ExecutionStatusChip key={key} execution={execution} label={label} />;
        return (
          <Chip key={key} size="small" label={`${label}: not run`} variant="outlined" sx={{ maxWidth: 420 }} />
        );
      })}
    </Box>
  );
}

function AutomationPanel({
  orgId,
  order,
  onChanged,
}: {
  orgId: string;
  order: ProspectOrderDetail;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const canSeeTranscripts = useCanSeeTranscripts();
  const runsQuery = useQuery({
    queryKey: keys.prospectOrder.agentRuns(order.id),
    queryFn: () => api.getProspectOrderAgentRuns(orgId, order.id),
    meta: { live: true },
  });
  const runs = runsQuery.data ?? [];
  const activeRun = runs.find((r) => isJobActive(r.status));

  const workflowsQuery = useQuery({
    queryKey: keys.prospectOrder.workflows(order.id),
    queryFn: () => api.getProspectOrderWorkflows(orgId, order.id),
    // Steps settle after the run finishes (Slack goes out via an outbox): the
    // order's own event then.
    meta: { live: true },
  });
  const workflows = workflowsQuery.data?.workflows ?? [];
  const automated = workflowsQuery.data?.automated ?? true;
  const activeWorkflows = workflows.filter((w) => w.active);
  const anyPending = workflows.some((w) => w.executions.some((e) => e.status === "pending"));
  const reviewable = workflows.flatMap((w) =>
    w.executions.filter((e) => e.status === "awaiting_approval" || e.status === "rejected"),
  );
  const refreshAfterDecision = () => {
    queryClient.invalidateQueries({ queryKey: keys.prospectOrder.workflows(order.id) });
    queryClient.invalidateQueries({ queryKey: keys.prospectOrder.agentRuns(order.id) });
    onChanged();
  };
  const hasRun = workflows.some((w) => w.executions.length > 0);
  const tenantSlug = workflowsQuery.data?.tenant_slug ?? order.tenant_slug;

  const runWorkflows = useMutation({
    mutationFn: () => api.runProspectOrderWorkflows(orgId, order.id),
    onSuccess: (result) => {
      toast.success(
        result.workflows.length === 1
          ? `Running "${result.workflows[0].name}"`
          : `Running ${result.workflows.length} workflows`,
      );
      queryClient.invalidateQueries({ queryKey: keys.prospectOrder.workflows(order.id) });
      queryClient.invalidateQueries({ queryKey: keys.prospectOrder.agentRuns(order.id) });
      onChanged();
    },
  });

  // An order a workflow made is worked by that workflow alone.
  const originWorkflowId = order.origin?.workflow_id;
  const workflowsHref = !tenantSlug
    ? null
    : originWorkflowId
      ? pathForTenant(tenantSlug, `/workflows/${originWorkflowId}`)
      : order.origin
        ? null
        : pathForTenant(
            tenantSlug,
            activeWorkflows.length ? "/workflows?trigger=prospect_order" : "/workflows/new?trigger=prospect_order",
          );

  return (
    <Panel
      title="Automation"
      subtitle="The tenant's prospect-order workflows and what they did on this order"
      action={
        <>
          {workflowsHref && (
            <Button component={RouterLink} to={workflowsHref} size="small">
              {order.origin ? "Edit workflow" : activeWorkflows.length ? "Edit workflows" : "Set up a workflow"}
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<Play />}
            onClick={() => runWorkflows.mutate()}
            // The label stays "Running…" for the whole run, not just the request.
            loading={!!activeRun || anyPending || runWorkflows.isPending}
            loadingPosition="start"
            disabled={!automated || !activeWorkflows.length}
          >
            {activeRun || anyPending ? "Running…" : hasRun ? "Run again" : "Run workflows"}
          </Button>
        </>
      }
    >
      {reviewable.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <StepApprovalBanner
            executions={reviewable}
            canDecide
            approve={(executionId) => api.approveProspectOrderStep(orgId, order.id, executionId)}
            onDecided={refreshAfterDecision}
          />
        </Box>
      )}

      {order.agent_model && (
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
          Agents on this order run on <strong>{order.agent_model}</strong>, as picked under Run agent.
        </Typography>
      )}

      {workflowsQuery.isLoading ? (
        <CenteredSpinner py={2} size={24} />
      ) : !automated ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Workflows run only for orders placed by the organization that owns{" "}
          {order.tenant_name ?? "this tenant"}; this one was placed by another organization,
          so it is worked by hand.
        </Typography>
      ) : workflows.length === 0 && order.origin ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          The workflow that created this order no longer exists; work it by hand.
        </Typography>
      ) : workflows.length === 0 ? (
        <EmptyState
          title="No prospect-order workflow yet"
          description={`Create one for ${order.tenant_name ?? "this tenant"} to have the prospecting agent work new orders, copy its results to a collection and post to Slack.`}
        />
      ) : (
        <Stack spacing={1}>
          {workflows.map((w) => (
            <WorkflowRow key={w.id} workflow={w} />
          ))}
        </Stack>
      )}

      {runs.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <SectionHeading title="Agent runs" count={runs.length} />
          <ScrollTable>
            <TableHead>
              <TableRow>
                {canSeeTranscripts && <TableCell sx={{ width: 48 }} />}
                <TableCell>Started</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell align="right">Turns</TableCell>
                <TableCell align="right">Tokens in / out</TableCell>
                <TableCell padding="checkbox" />
                {tenantSlug && <TableCell padding="checkbox" />}
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run) => (
                <AgentRunRow
                  key={run.id}
                  run={run}
                  variant="table"
                  fetchTranscript={(afterSeq) =>
                    api.getProspectOrderAgentRunTranscript(orgId, order.id, run.id, afterSeq)
                  }
                  actions={
                    tenantSlug ? (
                      // What this run found, in the tenant's Prospects inbox.
                      <RowActions
                        label="agent run"
                        primary={{
                          label: "Open in Prospects",
                          icon: <Mailbox />,
                          onClick: () =>
                            navigate(pathForTenant(tenantSlug, `/prospects/inbox?show=all&run=${run.id}`)),
                        }}
                      />
                    ) : undefined
                  }
                />
              ))}
            </TableBody>
          </ScrollTable>
        </>
      )}
    </Panel>
  );
}

const BACK = { to: "/organization/orders", label: "Prospect orders" };

function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { selectedOrgId } = useOrganization();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [commentBody, setCommentBody] = useState("");
  const [assigneeAnchor, setAssigneeAnchor] = useState<HTMLElement | null>(null);
  const [listOpen, setListOpen] = useState(false);

  const orderQuery = useQuery({
    queryKey: keys.prospectOrder.detail(id ?? ""),
    queryFn: () => api.getProspectOrder(selectedOrgId!, id!),
    enabled: !!selectedOrgId && !!id,
  });

  const invalidateOrder = () => {
    queryClient.invalidateQueries({ queryKey: keys.prospectOrder.detail(id ?? "") });
    queryClient.invalidateQueries({ queryKey: keys.prospectOrder.all });
  };

  const updateAssignees = useMutation({
    mutationFn: (userIds: string[]) =>
      api.updateProspectOrder(selectedOrgId!, id!, { assignee_user_ids: userIds }),
    onSuccess: invalidateOrder,
  });

  const attachList = useMutation({
    mutationFn: (listId: string) =>
      api.addProspectOrderList(selectedOrgId!, id!, listId),
    onSuccess: invalidateOrder,
  });

  const detachList = useMutation({
    mutationFn: (listId: string) =>
      api.removeProspectOrderList(selectedOrgId!, id!, listId),
    onSuccess: () => {
      invalidateOrder();
      toast.success("Collection detached");
    },
  });

  const addComment = useMutation({
    mutationFn: () => api.addProspectOrderComment(selectedOrgId!, id!, commentBody.trim()),
    onSuccess: () => {
      setCommentBody("");
      queryClient.invalidateQueries({ queryKey: keys.prospectOrder.detail(id ?? "") });
    },
  });

  const deleteOrder = useMutation({
    mutationFn: () => api.deleteProspectOrder(selectedOrgId!, id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.prospectOrder.all });
      toast.success("Order deleted");
      navigate("/organization/orders");
    },
  });

  if (!selectedOrgId) {
    return (
      <>
        <PageHeader back={BACK} title="Prospect order" />
        <NoSelectionPrompt kind="org" />
      </>
    );
  }
  if (orderQuery.isLoading || orderQuery.isError || !orderQuery.data) {
    return (
      <DetailPageStatus isLoading={orderQuery.isLoading} message="Order not found." back={BACK} />
    );
  }

  const order = orderQuery.data;

  return (
    <Stack spacing={3}>
      <PageHeader
        back={BACK}
        title={order.title}
        description={order.tenant_name ? `Prospect order for ${order.tenant_name}` : undefined}
        actions={
          <>
            <StatusChip kind="order" value={order.status} label={STATUS_LABELS[order.status]} />
            <Button variant="outlined" startIcon={<Pencil />} onClick={() => setEditOpen(true)}>
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Trash2 />}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          </>
        }
      />

      <Panel title="Brief">
        <DetailGrid minWidth={180}>
          <DetailRow label="Tenant" value={order.tenant_name ?? "-"} />
          <DetailRow
            label="Requested count"
            value={order.requested_count != null ? formatNumber(order.requested_count) : "-"}
          />
          <DetailRow
            label="Deadline"
            value={order.deadline ? <FormattedDate value={order.deadline} /> : "-"}
          />
          <DetailRow
            label="Prospects"
            value={
              order.new_prospects_only
                ? "New only, not seen before"
                : "Repeats allowed, marked as seen"
            }
          />
        </DetailGrid>

        <Divider sx={{ mt: 0.5, mb: 2 }} />

        {order.origin && (
          <Box sx={{ mb: 2 }}>
            <OrderOrigin order={order} />
          </Box>
        )}
        <DetailRow label="What the company sells" value={briefText(order.offering_text)} />
        <DetailRow label="Ideal customer profile" value={briefText(order.icp_text)} />
        <DetailRow label="Ideal buyer persona" value={briefText(order.buyer_persona_text)} />
        <DetailRow
          label="Assignees"
          value={
            <Box
              component="span"
              sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap", alignItems: "center" }}
            >
              {order.assignees.length === 0 && (
                <Box component="span" sx={{ color: "text.secondary" }}>
                  Unassigned
                </Box>
              )}
              {order.assignees.map((a) => (
                <Chip
                  key={a.user_id}
                  size="small"
                  avatar={
                    <UserAvatar
                      displayName={a.display_name}
                      email={a.email}
                      pictureUrl={a.picture_url}
                      size={24}
                    />
                  }
                  label={a.display_name || a.email}
                  onDelete={() =>
                    updateAssignees.mutate(
                      order.assignees
                        .filter((x) => x.user_id !== a.user_id)
                        .map((x) => x.user_id),
                    )
                  }
                />
              ))}
              <IconButton
                size="small"
                aria-label="add assignee"
                disabled={updateAssignees.isPending}
                onClick={(e) => setAssigneeAnchor(e.currentTarget)}
              >
                <Plus fontSize="small" />
              </IconButton>
            </Box>
          }
        />
        <Popover
          open={!!assigneeAnchor}
          anchorEl={assigneeAnchor}
          onClose={() => setAssigneeAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box sx={{ p: 2, width: 320 }}>
            <AssigneePicker
              orgId={selectedOrgId}
              value={order.assignees.map((a) => a.user_id)}
              onChange={(ids) => updateAssignees.mutate(ids)}
            />
          </Box>
        </Popover>
      </Panel>

      <AutomationPanel orgId={selectedOrgId} order={order} onChanged={invalidateOrder} />

      <Panel
        title="Collections"
        count={order.collections.length}
        disablePadding={order.collections.length > 0}
        action={
          <Stack direction="row" spacing={1}>
            {order.tenant_slug && order.collections.length > 0 && (
              // Deciding on what the agent found happens in the tenant's Prospects inbox.
              <Button
                component={RouterLink}
                to={pathForTenant(order.tenant_slug, `/prospects/inbox?show=all&order=${order.id}`)}
                size="small"
                startIcon={<Mailbox />}
              >
                Open in Prospects
              </Button>
            )}
            <Button
              size="small"
              startIcon={<Plus />}
              loading={attachList.isPending}
              loadingPosition="start"
              onClick={() => setListOpen(true)}
            >
              Add collection
            </Button>
          </Stack>
        }
      >
        {order.collections.length === 0 ? (
          <EmptyState
            title="No collections yet"
            description="Add one to collect this order's prospects."
          />
        ) : (
          <ScrollTable>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right">Prospects</TableCell>
                <TableCell align="right">Pending</TableCell>
                <TableCell align="right">Signals</TableCell>
                <TableCell>Last added</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {order.collections.map((pl) => (
                <TableRow key={pl.id} hover>
                  <TableCell>
                    <Link
                      component={RouterLink}
                      to={pathForTenant(order.tenant_slug, collectionPath(pl.id))}
                    >
                      {pl.name}
                    </Link>
                  </TableCell>
                  <TableCell align="right">{formatNumber(pl.prospect_count)}</TableCell>
                  <TableCell align="right">{formatNumber(pl.pending_count)}</TableCell>
                  <TableCell align="right">{formatNumber(pl.signal_trigger_count)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <RelativeTime value={pl.last_added_at} never="-" />
                  </TableCell>
                  <TableCell padding="checkbox" align="right">
                    <RowActions
                      label={pl.name}
                      actions={[
                        {
                          label: "Detach from order",
                          icon: <X fontSize="small" />,
                          destructive: true,
                          onClick: () => detachList.mutateAsync(pl.id),
                          confirm: {
                            title: "Detach collection",
                            confirmLabel: "Detach",
                            body: (
                              <>
                                Detach <strong>{pl.name}</strong> from this order? The collection and its
                                prospects are kept.
                              </>
                            ),
                          },
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ScrollTable>
        )}
      </Panel>

      <Panel title="Comments" count={order.comments.length}>
        <Stack spacing={2}>
          {order.comments.length === 0 && <EmptyState title="No comments yet" />}
          {order.comments.map((c) => (
            <Box key={c.id} sx={{ display: "flex", gap: 1.5 }}>
              <UserAvatar
                displayName={c.author?.display_name}
                email={c.author?.email}
                pictureUrl={c.author?.picture_url}
                size={32}
              />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {c.author?.display_name || c.author?.email || "Unknown"}{" "}
                  <Typography component="span" variant="caption" sx={{ color: "text.secondary" }}>
                    <RelativeTime value={c.created_at} />
                  </Typography>
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {c.body}
                </Typography>
              </Box>
            </Box>
          ))}
          <Divider />
          <TextField
            label="Add a comment"
            value={commentBody}
            onChange={(e) => setCommentBody(e.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <Box>
            <Button
              variant="contained"
              onClick={() => addComment.mutate()}
              disabled={!commentBody.trim()}
              loading={addComment.isPending}
            >
              Comment
            </Button>
          </Box>
        </Stack>
      </Panel>

      {editOpen && (
        <OrderFormDialog
          onClose={() => setEditOpen(false)}
          orgId={selectedOrgId}
          order={order}
          onSaved={() =>
            queryClient.invalidateQueries({ queryKey: keys.prospectOrder.detail(id ?? "") })
          }
        />
      )}
      <ListSelector
        open={listOpen}
        onClose={() => setListOpen(false)}
        tenantId={order.tenant_id}
        onSelect={(listId) => {
          attachList.mutate(listId);
          setListOpen(false);
        }}
      />
      <ConfirmDialog
        open={deleteOpen}
        title="Delete order"
        pending={deleteOrder.isPending}
        onConfirm={() => deleteOrder.mutate()}
        onClose={() => setDeleteOpen(false)}
      >
        Delete this prospect order? This cannot be undone.
      </ConfirmDialog>
    </Stack>
  );
}

export default OrderDetailPage;
